import { Link, createFileRoute } from '@tanstack/react-router'
import { Button, Card, Pill, Skeleton, fullTime, timeAgo } from '@algoboo/ui'
import { adminFeesQuery, adminKeys, groupFees, useAdminFees } from '../lib/admin'
import { useApiMutation } from '../lib/api'
import { CopyText } from '../components/CopyText'
import { useSession } from '../components/session'
import { AdminError, CopyButton, Table, Td } from '../components/admin/parts'
import { prefetchSignedIn } from '../lib/route-data'

export const Route = createFileRoute('/admin/fees')({
  loader: ({ context: { queryClient: qc } }) => prefetchSignedIn(qc, (me) => [me.isAdmin !== false && qc.ensureQueryData(adminFeesQuery())]),
  component: AdminFeesPage,
})

/** BACKLOG A3: the manual treasury → FeeRouter.collect backlog (docs/ECONOMICS.md open decision 7). */
function AdminFeesPage() {
  const { me } = useSession()
  const q = useAdminFees(!!me)
  const reconcile = useApiMutation('adminReconcileFees', { invalidate: () => [adminKeys.fees()] })
  const head = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="max-w-2xl text-[13px] text-ink-3">
        Fee rows not yet matched to a <span className="font-mono">FeeCollected</span>. Send each <span className="font-mono">collect</span> from the treasury with the full amount (the router splits it); the reconciler
        marks rows done once the event is indexed.
      </p>
      <Button size="sm" icon="refresh" loading={reconcile.isPending} onClick={() => reconcile.mutate({})}>
        {reconcile.isSuccess ? 'Reconcile queued' : 'Run reconcile'}
      </Button>
    </div>
  )
  if (q.isLoading) return <Skeleton className="h-64 rounded-xl" />
  if (q.error) return <AdminError error={q.error} refetch={() => void q.refetch()} />
  const groups = groupFees(q.data!)
  return (
    <div className="space-y-4" data-testid="admin-fees">
      {head}
      {groups.length === 0 && <Card className="px-4 py-8 text-center text-[13px] text-ink-3">Nothing to reconcile.</Card>}
      {groups.map((g) => (
        <section key={g.key} data-testid="admin-fee-group" data-agent={g.agent.handle} data-category={g.category} className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/a/$handle" params={{ handle: g.agent.handle }} className="text-[14px] font-semibold text-ink hover:underline">
              @{g.agent.handle}
            </Link>
            <Pill tone="neutral">{g.category}</Pill>
            <span className="font-mono text-[12.5px] text-ink-2">
              {g.rows.length} {g.rows.length === 1 ? 'row' : 'rows'} · ${g.totalUsd}
            </span>
            {g.rows[0]?.instruction.holderEligible ? <Pill tone="info">holders share</Pill> : <Pill tone="neutral">creator + platform</Pill>}
            <span className="ml-auto">
              <CopyButton value={g.calls} label={g.rows.length === 1 ? 'Copy call' : `Copy ${g.rows.length} calls`} />
            </span>
          </div>
          <Table head={['Collected', 'Source', 'Amount', 'Instruction']} caption={`Unreconciled ${g.category} fees of @${g.agent.handle}`}>
            {g.rows.map((r) => (
              <tr key={r.id} data-testid="admin-fee">
                <Td mono className="whitespace-nowrap text-ink-3">
                  <time dateTime={r.collectedAt} title={fullTime(r.collectedAt)}>
                    {timeAgo(r.collectedAt)}
                  </time>
                </Td>
                <Td mono className="text-ink-2" title={r.sourceRef}>
                  {r.source} · {r.sourceRef.length > 18 ? `${r.sourceRef.slice(0, 10)}…${r.sourceRef.slice(-6)}` : r.sourceRef}
                </Td>
                <Td mono>${r.amountUsd}</Td>
                <Td className="max-w-[560px]">
                  <CopyText value={r.instruction.call} />
                </Td>
              </tr>
            ))}
          </Table>
        </section>
      ))}
    </div>
  )
}
